import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useFeedback } from '../FeedbackProvider';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import FeedbackQuestion from './FeedbackQuestion';
import FeedbackProgress from './FeedbackProgress';
import SubmitReady from '../../NPOFeedback/SubmitReady';
import { mapFeedbackResolutionProgress } from './utils';
import FeedbackEmptyAnswer from './FeedbackEmptyAnswer';
import {
  isCustomQuestion,
  parseCustomQuestionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  CustomQuestion,
  CustomQuestionAnswer,
  CustomSingleQuestion,
} from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import FeedbackCustomQuestion from './FeedbackCustomQuestion';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

const getCustomQuestion = (
  customQuestions: CustomQuestion[],
  questionId: number | undefined,
):
  | {
      customQuestion: CustomQuestion;
      question: CustomSingleQuestion;
      questionIndex: number;
      customQuestionIndex: number;
      answer: CustomQuestionAnswer[];
      parsedQuestionId: number;
    }
  | undefined => {
  if (!questionId || !isCustomQuestion(questionId)) return undefined;
  const parsedQuestionId = parseCustomQuestionId(questionId);
  for (let i = 0; i < customQuestions.length; i++) {
    const customQuestion = customQuestions[i];
    for (let j = 0; j < customQuestion.questions.length; j++) {
      const question = customQuestion.questions[j];
      if (question.dbId === parsedQuestionId) {
        return {
          question,
          parsedQuestionId,
          customQuestion,
          questionIndex: j,
          customQuestionIndex: i,
          answer: question.answer,
        };
      }
    }
  }
  return undefined;
};

const StyledDiv = styled.div`
  .normal-font {
    font-weight: normal;
  }
  .custom-question-feedback {
    padding-bottom: 90px;
  }
  .feedback-progress {
    display: flex;
    width: 100%;
    gap: ${SPACERS.extraTiny};
    border-bottom: 1px solid ${COLORS.grayLight};
    padding: 0 ${SPACERS.small} ${SPACERS.medium} ${SPACERS.small};
    .section-part {
      flex: 1;
      cursor: pointer;
      height: ${SPACERS.tiny};
      &.resolved,
      &.verified {
        background-color: ${COLORS.success};
      }
      &.rejected {
        background-color: ${COLORS.critical};
      }
      &.unresolved {
        background-color: ${COLORS.grayLight};
      }
      &.selected {
        background-color: ${COLORS.ctaPrimary};
      }
      &:first-child {
        border-top-left-radius: ${SPACERS.extraTiny};
        border-bottom-left-radius: ${SPACERS.extraTiny};
      }
      &:last-child {
        border-top-right-radius: ${SPACERS.extraTiny};
        border-bottom-right-radius: ${SPACERS.extraTiny};
      }
    }
  }
`;

const FeedbackSection: React.FC = () => {
  const {
    reportId,
    feedback,
    feedbackQuestionsById,
    feedbackStatus,
    resolvedFeedback,
    verifyFeedback,
    feedbackByQuestions,
    answersById,
    customQuestions,
    guidanceText,
    programName,
    reportYear,
    donorName,
    reportStartDate,
    reportEndDate,
    comments,
    userType,
    updateAnswer,
    markAsDone,
    reloadFeedback,
    reloadAnswers,
  } = useFeedback();
  const length = feedbackByQuestions.length;
  const total =
    resolvedFeedback.questionIds.length || verifyFeedback.questionIds.length;

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    length === total
      ? undefined
      : () => {
          for (let index = 0; index < length; index++) {
            const q = feedbackByQuestions[index];
            const questionId = q.question?.questionId;
            const resolve = questionId
              ? resolvedFeedback.questionsById[questionId] ||
                verifyFeedback.questionsById[questionId]
              : undefined;
            if (!resolve) {
              return index;
            }
          }
          return undefined;
        },
  );

  const handleNext = useCallback(() => {
    setSelectedIndex(s =>
      s !== undefined
        ? s === length - 1
          ? length === total
            ? undefined
            : s
          : s + 1
        : 0,
    );
  }, [length, total, setSelectedIndex]);

  const handlePrevious = useCallback(() => {
    setSelectedIndex(s => (s !== undefined ? (s === 0 ? s : s - 1) : 0));
  }, [setSelectedIndex]);

  const question =
    selectedIndex !== undefined
      ? feedbackByQuestions[selectedIndex].question
      : undefined;
  const questionId =
    selectedIndex === undefined ? undefined : question?.questionId;

  const answer = questionId ? answersById[questionId] : undefined;
  const customQuestion = getCustomQuestion(customQuestions, questionId);

  const isDone = length === total;

  const handleDoneQuestion = useCallback(
    (isCustomQuestion: boolean) => {
      if (selectedIndex !== undefined) {
        markAsDone(
          feedbackByQuestions[selectedIndex],
          true,
          feedback.status,
          isCustomQuestion,
        );
      }
    },
    [selectedIndex, feedbackByQuestions, markAsDone],
  );

  const isLastIndex = selectedIndex === length - 1;
  return (
    <StyledDiv>
      <FeedbackProgress
        selectedIndex={selectedIndex}
        verifyFeedback={verifyFeedback}
        resolvedFeedback={resolvedFeedback}
        feedbackByQuestions={feedbackByQuestions}
        setSelectedIndex={setSelectedIndex}
      />
      {!customQuestion && questionId && selectedIndex !== undefined && (
        <FeedbackQuestion
          reportId={reportId}
          questionId={questionId}
          answersById={answersById}
          selectedIndex={selectedIndex}
          feedbackByQuestions={feedbackByQuestions}
          setSelectedIndex={setSelectedIndex}
          answer={
            answer || {
              dbId: questionId,
              questionId,
              text: 'Unknown',
              date1: '',
              date2: '',
              questionType: 'CUSTOM_OPTIONS',
              questionText: 'Unknown',
              question: {
                text: question?.questionText || '',
                questionType: 'CUSTOM_OPTIONS',
                dbId: questionId,
                section: '' as FeedbackSectionId,
              },
            }
          }
          guidanceText={guidanceText}
          reportYear={reportYear}
          reportStartDate={reportStartDate}
          reportEndDate={reportEndDate}
          programName={programName}
          donorName={donorName}
          isLastIndex={isLastIndex}
          isDone={isDone}
          feedback={feedback}
          updateAnswer={updateAnswer}
          feedbackQuestionsById={feedbackQuestionsById}
          comments={comments[questionId] || []}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          markAsDone={() => handleDoneQuestion(false)}
          reloadAnswers={reloadAnswers}
        />
      )}
      {customQuestion && selectedIndex !== undefined && (
        <FeedbackCustomQuestion
          reportId={reportId}
          feedback={feedback}
          comments={comments[customQuestion.parsedQuestionId] || []}
          customQuestion={customQuestion}
          handleDoneQuestion={() => handleDoneQuestion(true)}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          selectedIndex={selectedIndex}
          isDone={isDone}
          isLastIndex={isLastIndex}
          className="custom-question-feedback"
        />
      )}
      <FeedbackEmptyAnswer
        answer={answer}
        reportId={reportId}
        questionId={questionId}
        selectedIndex={selectedIndex}
      />
      {selectedIndex === undefined && (
        <SubmitReady
          total={length}
          isReady={true}
          reportId={reportId}
          userType={userType}
          isThirdPartyReport={feedback.cnOptedIn}
          feedbackStatus={feedbackStatus}
          progress={mapFeedbackResolutionProgress(
            feedbackStatus,
            resolvedFeedback,
            verifyFeedback,
          )}
          handleBack={handlePrevious}
          refreshFeedbackStatus={reloadFeedback}
        />
      )}
    </StyledDiv>
  );
};

export default FeedbackSection;
